/**
 * "Is this portal installed as an app, and can we install it right now?"
 *
 * One hook, so the floating card and the Profile panel can never disagree.
 *
 * Knowing the answer is harder than it sounds, and the honest answer has four
 * states, not two:
 *
 *   · `installed`  — certain. Either we ARE the installed app
 *     (display-mode: standalone, or iOS's own navigator.standalone), or
 *     Chromium's getInstalledRelatedApps() told us the app is on this device.
 *     That second check is why the manifest lists itself under
 *     `related_applications`: without that entry the API can't see us.
 *   · `available`  — certain it is NOT installed: Chromium only fires
 *     `beforeinstallprompt` for an app that meets the install criteria and is
 *     not already installed. We hold the event and can open the real dialog.
 *   · `manual`     — Safari. No install API at all, so it can neither install
 *     for us nor tell us whether it already did. We can only show the steps.
 *   · `probably`   — Chromium with NO install prompt. Chrome fires
 *     `beforeinstallprompt` only for an app it could install, so its silence
 *     on a browser that CAN install means, in almost every case, that the app
 *     is already there. This state exists because the first version lumped it
 *     in with `unknown` and told someone using Chrome, with the app installed,
 *     that their browser could not install it and they should try Chrome.
 *   · `unknown`    — a browser that cannot install at all (Firefox, an
 *     embedded webview). Only here is "open it in Chrome or Edge" true.
 *
 * The event itself is captured by a tiny inline script in index.html, because
 * Chromium fires it once — often before React has mounted — and refuses to
 * hand it over again.
 */
import { useEffect, useState } from 'react';

export type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

declare global {
  interface Window {
    __pwaPrompt?: InstallPrompt | null;
    __pwaInstalled?: boolean;
  }
  interface Navigator {
    getInstalledRelatedApps?: () => Promise<Array<{ platform?: string; url?: string; id?: string }>>;
  }
}

export type InstallState = 'installed' | 'available' | 'manual' | 'declined' | 'probably' | 'unknown';

/** Running AS the app: its own window, no browser furniture. */
export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const ios = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return ios || window.matchMedia('(display-mode: standalone)').matches;
}

/** Chromium — the family that can install a web app at all: Chrome, Edge,
 *  Brave, Opera, Arc. Firefox has no "Chrome" in its user agent and no way to
 *  install, which is the difference this test exists to draw. */
export function isChromium(): boolean {
  if (typeof navigator === 'undefined') return false;
  const brands = (navigator as Navigator & { userAgentData?: { brands?: { brand: string }[] } }).userAgentData?.brands;
  if (brands?.length) return brands.some((b) => /Chromium|Google Chrome|Microsoft Edge/i.test(b.brand));
  return /Chrome|Chromium|CriOS|Edg\//.test(navigator.userAgent) && !isAppleSafari();
}

/** Safari — the only engine with no install API, on the only platform where
 *  installing is also what switches push notifications on. */
export function isAppleSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const webkit = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return webkit && /iPad|iPhone|iPod|Macintosh/.test(ua);
}

export function useInstall() {
  const [standalone, setStandalone] = useState(isStandalone);
  const [installedElsewhere, setInstalledElsewhere] = useState(false);
  const [prompt, setPrompt] = useState<InstallPrompt | null>(
    () => (typeof window !== 'undefined' ? window.__pwaPrompt ?? null : null),
  );
  /* Chromium's dialog can be opened ONCE per event. After a refusal the event
     is spent, and offering the button again would be a button that does
     nothing. */
  const [refused, setRefused] = useState(false);
  /* Until the related-apps check answers, we say nothing rather than the
     wrong thing. */
  const [checked, setChecked] = useState(false);
  const safari = isAppleSafari();

  useEffect(() => {
    let alive = true;
    const onReady = () => setPrompt(window.__pwaPrompt ?? null);
    const onInstalled = () => { setInstalledElsewhere(true); setPrompt(null); setRefused(false); };
    const mq = window.matchMedia('(display-mode: standalone)');
    const onMode = () => setStandalone(isStandalone());

    window.addEventListener('pwa-prompt-ready', onReady);
    window.addEventListener('appinstalled', onInstalled);
    mq.addEventListener('change', onMode);
    if (window.__pwaInstalled) setInstalledElsewhere(true);

    /* Chromium only: does this device already have the app? */
    const ask = navigator.getInstalledRelatedApps;
    if (typeof ask === 'function') {
      ask.call(navigator)
        .then((apps) => { if (alive) { if (apps.length > 0) setInstalledElsewhere(true); setChecked(true); } })
        .catch(() => { if (alive) setChecked(true); });
    } else {
      setChecked(true);
    }

    return () => {
      alive = false;
      window.removeEventListener('pwa-prompt-ready', onReady);
      window.removeEventListener('appinstalled', onInstalled);
      mq.removeEventListener('change', onMode);
    };
  }, []);

  const installed = standalone || installedElsewhere;
  const state: InstallState = installed
    ? 'installed'
    : prompt
      ? 'available'
      : safari
        ? 'manual'
        /* The dialog was opened and refused: the prompt is spent, and
           Chrome's silence now means "declined", not "installed". */
        : refused
          ? 'declined'
          : isChromium()
            ? 'probably'
            : 'unknown';

  /** Opens the browser's real install dialog. Returns what happened, so the
   *  caller can say something true afterwards. */
  const install = async (): Promise<'accepted' | 'dismissed' | 'unavailable'> => {
    const p = prompt ?? window.__pwaPrompt ?? null;
    if (!p) return 'unavailable';
    await p.prompt();
    const choice = await p.userChoice.catch(() => null);
    window.__pwaPrompt = null;
    setPrompt(null);
    if (choice?.outcome === 'accepted') { setInstalledElsewhere(true); return 'accepted'; }
    setRefused(true);
    return 'dismissed';
  };

  return { state, installed, standalone, safari, refused, checked, install };
}
